import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import TabContent from './content/TabContent';
import Dialog from '../../../src/components/Dialog';
import bgImg from '../../assets/images/images-update/create-account/bg-img.png';
import AuthHeader from '../../components/header/AuthHeader';
import { useTrackingContext } from '../../context/trackingContext';
import { useUserContext } from '../../context/userContext';
import { useLocation, useNavigate } from 'react-router-dom';
import ButtonAuth from '../../components/ButtonAuth';
import Cookies from 'js-cookie';
import useClient from '../../hooks/useClient';
import config from '../../config';
import ndaDocument from '../../assets/docs/NON-DISCLOSURE AGREEMENT FOR EVERYWHERE TEST PHASE.pdf';
import { calculateAge } from '../../utils/calculateAge';
import getKeysByValue from '../../utils/getKeysByValue';
import Input from '../../components/Input';

const builderOptions = [
  { name: 'minecraft', title: 'Minecraft' },
  { name: 'roblox', title: 'Roblox' },
  { name: 'fortnite', title: 'Fortnite' },
  { name: 'theSims', title: 'The Sims' },
  { name: 'recRoom', title: 'Rec Room' },
  { name: 'garrysMod', title: "Garry's Mod" },
  { name: 'trackmania', title: 'Trackmania' },
  { name: 'core', title: 'Core' }
];

const Account = () => {
  const [activeTab, setActiveTab] = useState({
    id: 1,
    label: 'Account Information'
  });

  const navigate = useNavigate();
  const { register, handleSubmit, control, watch } = useForm({ mode: 'onTouched' });

  const [isAlphaThankYouOpen, setIsAlphaThankYouOpen] = useState(false);
  const [isCreateAccountOpen, setIsCreateAccountOpen] = useState(false);
  const [isBuilderPreferencesAcknowledged, setIsBuilderPreferencesAcknowledged] = useState(false);
  const [age, setAge] = useState(null);

  const {
    trackPageView,
    accountCreated,
    setAccountCreated,
    trackAccountCreate,
    trackClick,
    trackError
  } = useTrackingContext();
  const location = useLocation();

  const { fetchUser, setUserData, fetchLoginHistory, userData, fetchReferrals } = useUserContext();

  const handleTabChange = (id, label) => {
    setActiveTab({ id, label });
  };

  const client = useClient();

  const acceptTerms = async () => {
    await client.post(`${config.everywhereApi}/users/self/profile/accept-terms`);
    fetchUser();
  };

  const removeCookie = async () => {
    await client.get(`${config.everywhereApi}/users/logout`);
    Cookies.remove('_barbToken');
    setUserData();
    navigate('/login');
  };

  const onSubmit = async (data) => {
    setIsBuilderPreferencesAcknowledged(true);
    setIsCreateAccountOpen(false);

    try {
      const keys = getKeysByValue(data, true);
      const index = keys.indexOf('otherCheckbox');

      if (index > -1) {
        // remove otherCheckbox
        keys.splice(index, 1);

        if (data.other && data.other.length) {
          keys.push(data.other);
        }
      }

      const builderPreferences = keys.map((builderPreference) => {
        const option = builderOptions.find(
          (builderOption) => builderOption.name === builderPreference
        );
        if (option) return option.title;
        return builderPreference;
      });

      trackClick({
        btnName: 'Confirm Builder Preferences clicked',
        builderPreferences
      });

      await client.post(`${config.everywhereApi}/users/self/profile/update-builder-preferences`, {
        builderPreferences
      });
    } catch (err) {
      trackError({ error_text: 'error submiting builder pref' });
    } finally {
      fetchUser();
    }
  };

  useEffect(() => {
    if (accountCreated === 'alpha') {
      trackPageView({
        pageName: 'Thank you page visit (Alpha testing declined or inelegible)',
        accountCreationStep: 5.1
      });

      setIsAlphaThankYouOpen(true);
      setAccountCreated(false);
      trackAccountCreate(true);
    }

    if (accountCreated === 'decline') {
      trackPageView({
        pageName: 'Thank you page visit',
        accountCreationStep: 4.1
      });

      setIsCreateAccountOpen(true);
      setAccountCreated(false);
      trackAccountCreate(false);
    }

    trackPageView({
      pageName: 'Account page'
    });
  }, []);

  useEffect(() => {
    setUserData();
    fetchUser();
    fetchLoginHistory();
    fetchReferrals();
  }, []);

  useEffect(() => {
    const age = calculateAge(userData?.dob);
    setAge(age);
  }, [userData]);

  useEffect(() => {
    if (userData?.blocked) {
      trackPageView({
        pageName: 'Account Blocked page visit'
      });
    }
  }, [userData?.blocked]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);

    const tab = query.get('tab');

    if (tab) {
      handleTabChange(7, 'Link Accounts');
    }
  }, []);

  const accountLinks = [
    {
      id: 1,
      label: 'Account Information'
    },
    {
      id: 2,
      label: 'Games & Launcher'
    },
    {
      id: 3,
      label: 'Password and Security'
    },
    {
      id: 4,
      label: 'Communication Preferences'
    },
    // Conditional links waiting on user data
    {
      ...(userData?.referralsAvailable && {
        id: 5,
        label: 'Refer a Friend'
      })
    },
    {
      ...((userData?.externalAccountsLinkable || userData?.accountsLinkable) && {
        id: 7,
        label: 'Link Accounts'
      })
    }
  ];

  return (
    <div className="flex h-screen">
      <div
        className="lg:w-1/3 lg:h-full lg:bg-cover overflow-hidden lg:bg-no-repeat lg:relative lg:z-0 lg:block"
        style={{ backgroundImage: `url(${bgImg})`, backgroundPosition: 'top' }}>
        <AuthHeader loggedIn handleTabChange={handleTabChange} />

        <div className="bg-black h-full bg-opacity-85">
          <div className="hidden lg:flex justify-center h-3/5 items-center">
            <div className="p-4">
              <h1 className="text-white font-Korolev-Medium uppercase text-lg mb-4">Menu</h1>
              <ul className="tabs text-white">
                {accountLinks.map(({ id, label }) => (
                  <li
                    key={id}
                    className={`tab cursor-pointer mb-3 `}
                    onClick={() => handleTabChange(id, label)}>
                    <span
                      className={`font-Korolev-Medium ${
                        id === activeTab.id || (id === 1 && activeTab.id === 6) ? 'underline' : ''
                      } hover:underline`}>
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="content col-span-3 overflow-scroll h-full w-full">
        <TabContent activeContent={activeTab} handleTabChange={handleTabChange} />
      </div>
      <Dialog
        isOpen={isAlphaThankYouOpen}
        onClose={() => setIsAlphaThankYouOpen(false)}
        body={
          <>
            <h2 className="uppercase font-Korolev-Bold text-5xl mb-4">Thank you!</h2>
            <p className="mb-5">
              Thank you for applying to participate in the Closed Alpha testing.
              <br />
              <br />
              Should your application be successful, we will contact you closer to the start of the
              Closed Alpha test via email.
              <br />
              <br />
              Should you not be selected at this time, you may still be considered for future
              testing opportunities; you do not need to apply again.
            </p>
          </>
        }
      />

      <Dialog
        isOpen={
          !accountCreated &&
          userData &&
          !userData.validTermsAgreement &&
          (age === null || age >= 16)
        }
        onClose={() => {
          Cookies.remove('_barbToken');
          setUserData();
          navigate('/login');
        }}
        body={
          <>
            <h2 className="uppercase font-Korolev-Bold text-5xl mb-4">Updated Terms</h2>
            <p className="mb-10 leading-7">
              To access the Alpha phase, you must agree to the new{' '}
              <a
                className="underline"
                target="blank"
                href={'https://www.buildarocketboy.com/terms-of-use'}>
                Terms of Use
              </a>
              ,{' '}
              <a
                className="underline"
                target="blank"
                href={'https://www.buildarocketboy.com/privacy-notice'}>
                Privacy Notice
              </a>
              , and{' '}
              <a className="underline" target="blank" href={ndaDocument}>
                Non-Disclosure Agreement
              </a>
              . By clicking &quot;I agree&quot; below and accessing the Alpha Phase, you confirm
              that you have read and agree to the Terms of Use, Privacy Notice, and Non-Disclosure
              Agreement
            </p>
            <div className="flex flex-row justify-end">
              <ButtonAuth
                label="I Agree"
                className={'max-w-[150px]'}
                onClick={() => {
                  acceptTerms();
                }}
              />
            </div>
          </>
        }
      />

      <Dialog
        isOpen={userData?.blocked}
        onClose={() => {
          removeCookie();
        }}
        body={
          <>
            <h2 className="uppercase font-Korolev-Bold text-5xl mb-4">Account Blocked</h2>
            <p className="mb-5">
              Your account has been blocked for violations of our of Rules of Conduct and Terms of
              Use. <br /> <br />
              <b>Blocked Until:</b> {userData?.blocked}
            </p>

            <ButtonAuth
              label="Close"
              className={'max-w-[150px]'}
              onClick={() => {
                removeCookie();
              }}
            />
          </>
        }
      />
    </div>
  );
};

export default Account;
